import React from "react";
import { useQuery } from "react-query";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { getBudgetAnalysis } from "../components/Apis/apis";
import { API_RESP_STATUS, RUPEE_SYMBOL } from "../Constant/constant";
import moment from "moment";
import ProgressBar from "@ramonak/react-progress-bar";

const CategoryTransactionsDialog = ({ open, onClose, categoryName, startDate }) => {
  const { data, isLoading, error } = useQuery(
    ["categoryTransactions", categoryName, startDate],
    async () => {
      if (!open || !categoryName) return null;
      const showAll = true;
      const response = await getBudgetAnalysis(
        { name: categoryName },
        startDate,
        "",
        showAll
      );
      if (response.data.status === API_RESP_STATUS.success) {
        return response.data.payload;
      }
      return null;
    },
    {
      enabled: !!open && !!categoryName,
    }
  );

  // Extracted item details
  const categoryData = data?.data?.[0];
  const spent = categoryData?.spent || 0;
  const budgetLimit = categoryData?.budgetDetail?.limit;
  const icon = categoryData?.iconDetail?.icon;
  const transactions = categoryData?.transactions || [];

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      open={open}
      onClose={onClose}
      aria-labelledby="category-transactions-title"
    >
      <DialogTitle id="category-transactions-title" style={{ paddingBottom: "8px" }}>
        <div className="d-flex align-items-center gap-3">
          {icon && (
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "var(--bg-surface-hover)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid var(--border-color)",
              }}
            >
              <img
                style={{ height: "28px", width: "28px" }}
                src={icon}
                alt={categoryName}
              />
            </div>
          )}
          <div>
            <div style={{ fontWeight: "700", fontSize: "1.2rem" }}>
              {categoryName}
            </div>
            <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
              {startDate}
            </div>
          </div>
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingTop: "12px" }}>
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="text-danger text-center py-4">
            Failed to load transactions.
          </div>
        ) : (
          <div>
            {/* Budget Progress Card */}
            <div
              className="p-3 rounded-3 mb-4"
              style={{
                background: "var(--bg-surface)",
                border: "1px solid var(--border-color)",
              }}
            >
              <div className="d-flex justify-content-between align-items-baseline mb-2">
                <div>
                  <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Total Spent
                  </span>
                  <div style={{ fontSize: "1.4rem", fontWeight: "700", color: categoryData?.type === 1 ? "var(--danger)" : "var(--success)" }}>
                    {RUPEE_SYMBOL}{spent.toLocaleString("en-IN")}
                  </div>
                </div>
                {budgetLimit && (
                  <div className="text-end">
                    <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Budget Limit
                    </span>
                    <div style={{ fontSize: "1.1rem", fontWeight: "600" }}>
                      {RUPEE_SYMBOL}{budgetLimit.toLocaleString("en-IN")}
                    </div>
                  </div>
                )}
              </div>

              {budgetLimit ? (
                <div style={{ width: "100%", marginTop: "12px" }}>
                  <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.8rem" }}>
                    <span style={{ color: "var(--text-secondary)" }}>
                      Budget usage: {((spent / budgetLimit) * 100).toFixed(0)}%
                    </span>
                    <span style={{ color: spent > budgetLimit ? "var(--danger)" : "var(--text-secondary)" }}>
                      {spent > budgetLimit
                        ? `Over budget by ${RUPEE_SYMBOL}${(spent - budgetLimit).toLocaleString("en-IN")}`
                        : `${RUPEE_SYMBOL}${(budgetLimit - spent).toLocaleString("en-IN")} remaining`}
                    </span>
                  </div>
                  <ProgressBar
                    completed={Math.min(parseFloat(((spent / budgetLimit) * 100).toFixed(1)), 100)}
                    maxCompleted={100}
                    width="100%"
                    bgColor={spent > budgetLimit ? "var(--danger)" : "var(--primary)"}
                    baseBgColor="var(--border-color)"
                    height="8px"
                    customLabel={" "}
                  />
                </div>
              ) : null}
            </div>

            {/* Transactions Header */}
            <h6 style={{ fontWeight: "700", marginBottom: "12px", letterSpacing: "0.02em" }}>
              Transaction History
            </h6>

            {/* Transactions List */}
            {transactions.length === 0 ? (
              <div
                className="text-center py-4 rounded-3"
                style={{
                  background: "var(--bg-surface)",
                  border: "1px dashed var(--border-color)",
                  color: "var(--text-muted)",
                }}
              >
                No transactions recorded for this period.
              </div>
            ) : (
              <div
                className="list-group"
                style={{
                  border: "1px solid var(--border-color)",
                  borderRadius: "12px",
                }}
              >
                {transactions.map((tx) => (
                  <div
                    key={tx.transactionId}
                    className="list-group-item d-flex align-items-center justify-content-between"
                    style={{
                      borderBottom: "1px solid var(--border-color)",
                      background: "transparent",
                      padding: "12px 16px",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "600", fontSize: "0.95rem" }}>
                        {tx.accountDetails?.name || "Account"}
                      </div>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                          {moment(tx.date).format("DD MMM YYYY")}
                        </span>
                        {tx.notes && (
                          <>
                            <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>•</span>
                            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                              {tx.notes}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: "700",
                        color: tx.type === 1 ? "var(--danger)" : "var(--success)",
                      }}
                    >
                      {tx.type === 1 ? `-${RUPEE_SYMBOL}${tx.amount.toLocaleString("en-IN")}` : `+${RUPEE_SYMBOL}${tx.amount.toLocaleString("en-IN")}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryTransactionsDialog;
